import { ref, computed, type Ref } from 'vue';
import { cloneDeep, omit } from 'lodash';

import { useCustomer } from '@/use/customer';
import { useAddress } from '@/use/address';
import { sendGet } from '@/Utils/api';
import type { LocationItem, RawPlaceSearchResult, RideHistoryRow } from '@/models/rideOrdering';
import type { Venue } from '@/models/venue';
import { ORIGINAL_ADDRESS_ITEM } from '@/components/UIComponents/OrderingForms/venueInfo';
import { getMatchingLocation, getMatchingLocations, getRecentLocations, getSavedAddressIdentifier } from './recentRideLocations';

type AddressListItem = {
  id: string;
  key?: string;
  label?: string;
  address?: {
    name?: string;
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    zip?: string;
    latitude?: number | string | null;
    longitude?: number | string | null;
    id?: string | number | null;
  };
};

export type SelectedLocationContext = {
  autoSaveLocation: boolean;
  savedAddressIdentifier?: string;
  matchingSavedLocation: LocationItem | null;
  lastSuccessfulPickup: LocationItem | null;
  cancelledPickupLocations: LocationItem[];
};

const RECENT_RIDES_LIMIT = 50;

const { customerInfo, anchorBound, addressList } = useCustomer();
const addressComposable = useAddress();

export const streetViewTarget: Ref<'pickup' | 'dropoff'> = ref('pickup');

export const pickupLocation: Ref<LocationItem | null> = ref(null);
export const pickupVenuesPlaces: Ref<Venue[]> = ref([]);
export const pickupVenueNote = ref('');
export const pickupEntranceOptionKey = ref(ORIGINAL_ADDRESS_ITEM.key);
export const pickupLocationContext: Ref<SelectedLocationContext | null> = ref(null);

export const dropOffLocation: Ref<LocationItem | null> = ref(null);
export const dropoffVenuesPlaces: Ref<Venue[]> = ref([]);
export const dropoffVenueNote = ref('');
export const dropoffEntranceOptionKey = ref(ORIGINAL_ADDRESS_ITEM.key);
export const dropoffLocationContext: Ref<SelectedLocationContext | null> = ref(null);

export const stops: Ref<(LocationItem | null)[]> = ref([]);
export const stopVenuesPlaces: Ref<Venue[][]> = ref([]);
export const stopEntranceOptionKeys: Ref<string[]> = ref([]);
export const isSelectingStopAddress = computed(
  () => stops.value.length > 0 && stops.value[stops.value.length - 1] == null,
);

export const rawSearchResults: Ref<RawPlaceSearchResult[]> = ref([]);
export const recentPickupLocations: Ref<LocationItem[]> = ref([]);
export const cancelledPickupLocations: Ref<LocationItem[]> = ref([]);
export const isLoadingVenues = ref(false);

const pickupAddressAnchorBound = computed(() =>
  addressComposable.getAnchorBound({
    latitude: pickupLocation.value?.latitude,
    longitude: pickupLocation.value?.longitude,
  }),
);

const dropoffAddressAnchorBound = computed(() =>
  addressComposable.getAnchorBound({
    latitude: dropOffLocation.value?.latitude,
    longitude: dropOffLocation.value?.longitude,
  }),
);

// Computed
export const computedAnchorBound = computed(
  () => pickupAddressAnchorBound.value || dropoffAddressAnchorBound.value || anchorBound.value || undefined,
);

export const homeAddress = computed<LocationItem | null>(() => {
  const homeItem = addressList.value.find((item: AddressListItem) => item.id === 'home');
  // Coordinates may be null here for a PII-masked caller (the backend nulls lat/lng on masked
  // addresses) — still surface the Home option so it can be selected; the server backfills the
  // real coordinates from the caller's stored address when the estimate request comes in.
  if (!homeItem?.address) return null;

  const addr = homeItem.address;
  return {
    id: 'home',
    name: addr?.name || 'Home',
    label: 'H',
    streetAddress: addr?.streetAddress,
    streetAddress2: addr?.streetAddress2,
    city: addr?.city,
    state: addr?.state,
    zip: addr?.zip,
    latitude: addr?.latitude ? Number(addr.latitude) : null,
    longitude: addr?.longitude ? Number(addr.longitude) : null,
    category: 'saved',
    source: 'saved',
    savedAddressIdentifier: 'home',
  };
});

export const savedLocations = computed<LocationItem[]>(() => {
  if (!customerInfo.value?.id) return [];

  return addressList.value
    .filter((item: AddressListItem) => item.id !== 'home' && item.id !== 'newLocation')
    .map((item: AddressListItem, index: number) => {
      const addr = item.address;
      return {
        id: item.id,
        name: item.id === 'lastRideLocation' ? 'Last Drop-off' : addr?.name || item.label?.split(' - ')[0] || 'Location',
        label: item.id === 'lastRideLocation' ? 'L' : String(index + 2),
        streetAddress: addr?.streetAddress,
        streetAddress2: addr?.streetAddress2,
        city: addr?.city,
        state: addr?.state,
        zip: addr?.zip,
        latitude: addr?.latitude ? Number(addr.latitude) : null,
        longitude: addr?.longitude ? Number(addr.longitude) : null,
        category: 'saved',
        source: 'saved',
        savedAddressIdentifier:
          item.id.startsWith('customAddress') && addr?.id != null
            ? String(addr.id)
            : getSavedAddressIdentifier({ id: item.id } as LocationItem),
      };
    });
});

export const searchedLocations = computed<LocationItem[]>(() => {
  return rawSearchResults.value.map((result, index) => {
    const address = addressComposable.parseAddressComponentsToAddress(result.details.address_components);
    return {
      id: result.placeId,
      name: result.details.name || result.prediction.structured_formatting?.main_text || result.prediction.description || '',
      label: String(index + 1),
      streetAddress: address.streetAddress || result.prediction.structured_formatting?.secondary_text || '',
      city: address.city,
      state: address.state,
      zip: address.zip,
      latitude: result.details.geometry?.location?.lat() ?? null,
      longitude: result.details.geometry?.location?.lng() ?? null,
      category: 'searched',
      source: 'search',
    };
  });
});

export const displayedLocations = computed<LocationItem[]>(() => {
  return searchedLocations.value.length > 0 ? searchedLocations.value : [...savedLocations.value, ...recentPickupLocations.value];
});

export const hasSelectedLocations = computed(() => !!(pickupLocation.value && dropOffLocation.value));

export const tripDistance = computed(() => {
  if (!pickupLocation.value?.latitude || !pickupLocation.value?.longitude) return 0;
  if (!dropOffLocation.value?.latitude || !dropOffLocation.value?.longitude) return 0;

  return addressComposable.getDistance(
    Number(pickupLocation.value.latitude),
    Number(pickupLocation.value.longitude),
    Number(dropOffLocation.value.latitude),
    Number(dropOffLocation.value.longitude),
  );
});

const isOutsideUS = (location: LocationItem | null) =>
  addressComposable.checkOutsideUSAddress({
    streetAddress: location?.streetAddress ?? '',
    city: location?.city ?? '',
    state: location?.state ?? '',
    zip: location?.zip ?? '',
    country: location?.country ?? '',
  });

export const isRideOutsideUS = computed(() => Boolean(isOutsideUS(pickupLocation.value) || isOutsideUS(dropOffLocation.value)));

export const formatLocationAddress = (location: LocationItem | null): string => {
  if (!location) return '';
  return addressComposable.getFormattedAddress({
    streetAddress: location.streetAddress ?? '',
    streetAddress2: location.streetAddress2 ?? '',
    city: location.city ?? '',
    state: location.state ?? '',
    zip: location.zip ?? '',
  });
};

export const getDistanceFromHome = (location: LocationItem): number => {
  if (!homeAddress.value?.latitude || !homeAddress.value?.longitude) return 0;
  if (!location.latitude || !location.longitude) return 0;

  return addressComposable.getDistance(
    Number(homeAddress.value.latitude),
    Number(homeAddress.value.longitude),
    Number(location.latitude),
    Number(location.longitude),
  );
};

export const formatDistance = (miles: number): string => {
  if (miles === 0) return '0 ft';
  if (miles < 0.1) {
    const feet = Math.round(miles * 5280);
    return `${feet} ft`;
  }
  return `${miles.toFixed(1)} mi`;
};

export const getRawSearchResultByPlaceId = (placeId: string): RawPlaceSearchResult | undefined => {
  return rawSearchResults.value.find((r) => r.placeId === placeId);
};

export const loadRecentRideLocations = async (callerId: string) => {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const [completedResult, cancelledResult] = await Promise.allSettled([
    sendGet('rides', { callerId, status: 'completed', limit: RECENT_RIDES_LIMIT, offset: 0, start: oneYearAgo.toISOString() }),
    sendGet('rides', { callerId, driverCancels: true, limit: RECENT_RIDES_LIMIT, offset: 0, start: oneYearAgo.toISOString() }),
  ]);

  if (customerInfo.value?.id !== callerId) {
    return;
  }

  recentPickupLocations.value =
    completedResult.status === 'fulfilled'
      ? getRecentLocations((completedResult.value.data?.rows ?? []) as RideHistoryRow[], 'recent')
      : [];
  cancelledPickupLocations.value =
    cancelledResult.status === 'fulfilled'
      ? getRecentLocations((cancelledResult.value.data?.rows ?? []) as RideHistoryRow[], 'cancelled')
      : [];
};

const geocodeAddress = (address: string): Promise<{ latitude: number; longitude: number } | null> => {
  if (!address || !window.google?.maps?.Geocoder) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    new window.google.maps.Geocoder().geocode({ address }, (results, status) => {
      const point = results?.[0]?.geometry?.location;
      if (status !== 'OK' || !point) {
        resolve(null);
        return;
      }

      resolve({ latitude: point.lat(), longitude: point.lng() });
    });
  });
};

export const getVenuesPlaces = async (location: LocationItem, rideType: string) => {
  if (location.latitude == null || location.longitude == null) {
    return [];
  }

  isLoadingVenues.value = true;
  try {
    const response = await sendGet('venues', {
      lat: location.latitude,
      lng: location.longitude,
      rideType,
      includeAccessPoints: true,
    });

    return response.data;
  } catch {
    return [];
  } finally {
    isLoadingVenues.value = false;
  }
};

const getSavedLocationCandidates = (): LocationItem[] => {
  return [
    ...(homeAddress.value ? [homeAddress.value] : []),
    ...savedLocations.value.filter((location) => location.id !== 'lastRideLocation'),
  ];
};

const getLocationContext = (location: LocationItem): SelectedLocationContext => {
  const savedLocation = location.category === 'saved' ? location : getMatchingLocation(location, getSavedLocationCandidates());
  const successfulPickup = getMatchingLocation(location, recentPickupLocations.value);
  const cancellationLocations = successfulPickup ? [] : getMatchingLocations(location, cancelledPickupLocations.value);

  return {
    autoSaveLocation: true,
    savedAddressIdentifier: savedLocation ? getSavedAddressIdentifier(savedLocation) : undefined,
    matchingSavedLocation: savedLocation,
    lastSuccessfulPickup: successfulPickup,
    cancelledPickupLocations: cancellationLocations,
  };
};

const applyKnownLocationData = (location: LocationItem, context: SelectedLocationContext): LocationItem => {
  return {
    ...location,
    ...(context.matchingSavedLocation && location.category !== 'saved'
      ? {
          name: context.matchingSavedLocation.name,
          savedAddressIdentifier: getSavedAddressIdentifier(context.matchingSavedLocation),
        }
      : {}),
  };
};

export const selectLocation = (
  location: LocationItem,
  venues: Venue[] = [],
  buildingCoordinates?: { latitude: number; longitude: number },
) => {
  const locationContext = getLocationContext(location);
  const selectedLocation = {
    ...omit(applyKnownLocationData(location, locationContext), ['category']),
    ...(buildingCoordinates ?? {}),
  };

  if (!pickupLocation.value) {
    pickupLocation.value = selectedLocation;
    pickupVenuesPlaces.value = venues;
    pickupEntranceOptionKey.value = ORIGINAL_ADDRESS_ITEM.key;
    pickupLocationContext.value = locationContext;
  } else if (isSelectingStopAddress.value) {
    const activeIndex = stops.value.length - 1;
    stops.value.splice(activeIndex, 1, omit(location, ['category']));
    stopVenuesPlaces.value.splice(activeIndex, 1, venues);
    stopEntranceOptionKeys.value.splice(activeIndex, 1, ORIGINAL_ADDRESS_ITEM.key);
  } else if (!dropOffLocation.value) {
    dropOffLocation.value = selectedLocation;
    dropoffVenuesPlaces.value = venues;
    dropoffEntranceOptionKey.value = ORIGINAL_ADDRESS_ITEM.key;
    dropoffLocationContext.value = locationContext;
  }
};

// Saved locations already have stored coordinates. Geocoding supplies the separate Google building point,
// and Uber venues are requested for that point so both remain selectable.
export const selectLocationWithOptions = async (location: LocationItem, rideType: string) => {
  const isPickupOrDropoff = !isSelectingStopAddress.value && (!pickupLocation.value || !dropOffLocation.value);
  const buildingCoordinates =
    isPickupOrDropoff && location.category === 'saved' ? await geocodeAddress(formatLocationAddress(location)) : null;
  const venueLocation = buildingCoordinates ? { ...location, ...buildingCoordinates } : location;
  const venues = await getVenuesPlaces(venueLocation, rideType);
  selectLocation(location, venues, buildingCoordinates ?? undefined);
};

export const clearPickup = () => {
  pickupVenuesPlaces.value = [];
  pickupLocation.value = null;
  pickupVenueNote.value = '';
  pickupEntranceOptionKey.value = ORIGINAL_ADDRESS_ITEM.key;
  pickupLocationContext.value = null;
};

export const clearDropoff = () => {
  dropoffVenuesPlaces.value = [];
  dropOffLocation.value = null;
  dropoffVenueNote.value = '';
  dropoffEntranceOptionKey.value = ORIGINAL_ADDRESS_ITEM.key;
  dropoffLocationContext.value = null;
};

export const clearMultiStops = () => {
  stops.value = [];
  stopVenuesPlaces.value = [];
  stopEntranceOptionKeys.value = [];
};

export const resetRideLocations = () => {
  clearPickup();
  clearDropoff();
  clearMultiStops();
  recentPickupLocations.value = [];
  cancelledPickupLocations.value = [];
};

export const swapLocations = () => {
  const temp = pickupLocation.value;
  pickupLocation.value = dropOffLocation.value;
  dropOffLocation.value = temp;

  const tempLocationContext = pickupLocationContext.value;
  pickupLocationContext.value = dropoffLocationContext.value;
  dropoffLocationContext.value = tempLocationContext;

  const tempVenues = cloneDeep(pickupVenuesPlaces.value);
  pickupVenuesPlaces.value = cloneDeep(dropoffVenuesPlaces.value);
  dropoffVenuesPlaces.value = cloneDeep(tempVenues);

  const tempEntranceKey = pickupEntranceOptionKey.value;
  pickupEntranceOptionKey.value = dropoffEntranceOptionKey.value;
  dropoffEntranceOptionKey.value = tempEntranceKey;
};
